/**
 * Plain language for machine codes.
 *
 * The engine answers with a closed rejection set (doc 02 §2.9/§2.10) and the
 * edge answers with protocol errors. Neither belongs in front of a trader
 * verbatim: `E_NO_QUOTE` in a dialog is how support tickets start. Every code
 * that can reach this UI has exactly one entry here, typed as a
 * `Record<RejectionCode, …>` so adding a code to the frozen set is a **compile
 * error** until it is given words.
 *
 * Two rules the copy follows:
 *
 * 1. Say what happened and what the trader can do, not which guard fired.
 * 2. Keep the code visible in a muted reference line. A dealer reading a screen
 *    share needs it, and the code is the audit key; hiding it entirely is the
 *    opposite failure.
 *
 * `E_NO_QUOTE` is worded as a normal condition, not an error: docs/10 §10.5
 * says so explicitly, and a stale feed is not the trader's mistake.
 */

import { describeRejection } from '@weke/domain';
import type { RejectionCategory, RejectionCode } from '@weke/domain';

export interface Explanation {
  /** Short headline: what happened. */
  readonly title: string;
  /** One sentence: why, and what to do next. */
  readonly detail: string;
  readonly category: RejectionCategory | 'PROTOCOL';
  /** Where the code is specified, for the audit line. */
  readonly source: string;
}

interface Copy {
  readonly title: string;
  readonly detail: string;
}

const REJECTIONS: Record<RejectionCode, Copy> = {
  E_MARKET_CLOSED: {
    title: 'Market is closed',
    detail: 'This instrument is outside its trading session. The order was not sent.',
  },
  E_SYMBOL_DISABLED: {
    title: 'Instrument not available',
    detail: 'Your account group cannot trade this instrument right now.',
  },
  E_NO_QUOTE: {
    title: 'Prices unavailable',
    detail:
      'The price feed is stale, so the order was not priced. It will work again as soon as quotes resume.',
  },
  E_INVALID_VOLUME: {
    title: 'Volume not allowed',
    detail: 'The size is outside the range for this instrument, or not on its volume step.',
  },
  E_INVALID_PRICE: {
    title: 'Price not accepted',
    detail: 'That price is not on the instrument’s price steps.',
  },
  E_PRICE_FAR: {
    title: 'Too close to the market',
    detail: 'The price is inside the minimum distance from the current market price.',
  },
  E_NO_MONEY: {
    title: 'Not enough free margin',
    detail:
      'Free margin does not cover the required margin and commission for this order. Close or reduce a position, or use a smaller size.',
  },
  E_MAX_POSITIONS: {
    title: 'Position limit reached',
    detail: 'Your account is at its limit for open positions or working orders.',
  },
  E_VOLUME_LIMIT: {
    title: 'Volume limit reached',
    detail: 'This order would exceed the maximum volume allowed per order or per instrument.',
  },
  E_TIF_NOT_ALLOWED: {
    title: 'Time in force not allowed',
    detail: 'A market order can only be Immediate or Fill-or-kill.',
  },
  E_ORDER_TYPE_NOT_ALLOWED: {
    title: 'Order type not allowed',
    detail: 'This instrument does not accept that order type.',
  },
  E_DUPLICATE_CL_ORD_ID: {
    title: 'Duplicate request',
    detail: 'A different order was already sent with the same reference. Nothing was changed.',
  },
  E_ACCOUNT_DISABLED: {
    title: 'Account restricted',
    detail: 'This account is read-only, disabled or archived. Contact your broker.',
  },
  E_TRADE_DISABLED: {
    title: 'Trading is switched off',
    detail: 'Trading is disabled for your account group right now.',
  },
  E_NOT_ENOUGH_MARGIN_FOR_PENDING: {
    title: 'Not enough margin for a resting order',
    detail: 'Your free margin does not cover leaving this order working until it triggers.',
  },
  E_PRICE_SLIPPAGE: {
    title: 'Price moved',
    detail: 'The market moved beyond your tolerance, so nothing was filled. Review the price and send again.',
  },
  E_DEALER_REJECTED: {
    title: 'Dealer rejected the request',
    detail: 'The dealing desk declined this request. Nothing was filled.',
  },
  E_INTERNAL: {
    title: 'Something went wrong',
    detail:
      'The order was not accepted because of an internal error. It is safe to send it again — the reference prevents a duplicate fill.',
  },
  E_FILL_OR_KILL: {
    title: 'Could not fill in full',
    detail: 'There was not enough liquidity to fill the whole size immediately, so nothing was executed.',
  },
  E_EXTERNAL_TIMEOUT: {
    title: 'Venue did not confirm',
    detail: 'The external venue did not confirm the order in time, so it was cancelled.',
  },
  E_NO_CONVERSION: {
    title: 'Currency conversion unavailable',
    detail: 'There is no conversion path from this instrument’s currency to your account currency.',
  },
  E_TENANT_QUOTA: {
    title: 'Broker capacity reached',
    detail: 'Your broker’s platform is at capacity. This is not about your order — try again shortly.',
  },
  E_MARGIN_CALL: {
    title: 'Margin call',
    detail:
      'Your margin level is at or below the margin-call level, so new positions are blocked. Closing positions is still allowed.',
  },
  E_ORDER_STATE: {
    title: 'Order has already finished',
    detail: 'This order is no longer working — it was filled, cancelled or expired.',
  },
  /*
   * P12.1 (docs/19 §3). Distinct from its neighbours on purpose: the price the
   * trader saw is no longer available beyond the tolerance their group allows,
   * so **nothing was sent to the market** and the same request may be re-sent.
   * It is not `E_NO_QUOTE` (prices are fine — they simply moved) and not
   * `E_PRICE_SLIPPAGE` (that is a requote the trader may accept).
   */
  E_PRICE_CHANGED: {
    title: 'Price changed',
    detail:
      'The market moved further than this instrument allows between the price you saw and the price available now, so nothing was filled. Send again to trade at the current price.',
  },
  /*
   * P12.5 (docs/19 §3). A feed disagreement, not a market move: the broker's two
   * price sources did not agree when one replaced the other, so **new positions**
   * are paused until the feeds reconverge. Closing is always allowed, and the
   * wording says so — a client must never think it is trapped in a position.
   */
  E_CROSS_SOURCE_GUARD: {
    title: 'Trading paused on this instrument',
    detail:
      'Our two price sources disagreed when the feed switched, so new positions are paused briefly. Closing or reducing a position is not affected.',
  },
};

const SERVICE_ERRORS: Record<string, Copy> = {
  E_AUTH: {
    title: 'Session expired',
    detail: 'Sign in again. Your layout and any open panels are kept.',
  },
  E_TENANT: {
    title: 'Session does not match this account',
    detail: 'The request was refused because it named a different tenant or account. Sign in again.',
  },
  E_UNAVAILABLE: {
    title: 'Trading service reconnecting',
    detail: 'Your command was not acknowledged. It is safe to send it again — duplicates are refused.',
  },
  E_BUSY: {
    title: 'Platform busy',
    detail:
      'The ledger could not admit the command in time, so nothing was evaluated. It is safe to retry in a moment.',
  },
  E_PROTOCOL: {
    title: 'Request rejected by the server',
    detail: 'The command did not match the platform’s protocol, so nothing was sent to the market.',
  },
  E_RATE_LIMIT: {
    title: 'Too many requests',
    detail: 'Slow down for a moment; the platform is protecting the desk from a burst.',
  },
  E_NOT_FOUND: {
    title: 'Not found',
    detail: 'The order or position no longer exists on the server.',
  },
  E_INTERNAL: {
    title: 'Server error',
    detail: 'The server could not complete the request. Nothing was changed.',
  },
};

/** Explain an engine rejection code (exhaustive over the closed set). */
export function explainRejection(code: string): Explanation {
  if (isRejectionCode(code)) {
    const copy = REJECTIONS[code];
    const meta = describeRejection(code);
    return { title: copy.title, detail: copy.detail, category: meta.category, source: meta.doc };
  }
  return {
    title: 'Order rejected',
    detail: 'The engine refused this order. The reference is shown below for support.',
    category: 'PROTOCOL',
    source: 'unknown code',
  };
}

function isRejectionCode(value: string): value is RejectionCode {
  return Object.prototype.hasOwnProperty.call(REJECTIONS, value);
}

/** Explain a protocol-level error (`ERROR` frame or an HTTP failure). */
export function explainServiceError(code: string): Explanation {
  const copy = SERVICE_ERRORS[code] ?? {
    title: 'Request failed',
    detail: 'The platform could not complete the request. Nothing was changed.',
  };
  return { ...copy, category: 'PROTOCOL', source: code };
}

const ALERTS: Record<string, string> = {
  MARGIN_CALL: 'Margin call — your margin level has reached the call threshold.',
  STOP_OUT: 'Stop out — the platform closed positions to restore your margin level.',
  UNVALUABLE_BOOK: 'Positions cannot be valued: a price is missing or stale.',
  ROLLOVER_SKIPPED: 'Rollover was skipped for a position because a price was missing.',
  ENGINE_REFUSED: 'The engine refused to load this account. Trading is suspended.',
  FEED_STALE: 'The price feed is stale. Orders will be refused until prices resume.',
};

export function explainAlert(kind: string): string {
  return ALERTS[kind] ?? `Platform alert: ${kind}`;
}

/** A label for an order state, for the orders table. */
export const ORDER_STATE_LABEL: Record<string, string> = {
  RECEIVED: 'Received',
  ACTIVE: 'Working',
  PARTIALLY_FILLED: 'Partial',
  FILLED: 'Filled',
  CANCELED: 'Cancelled',
  REJECTED: 'Rejected',
  EXPIRED: 'Expired',
};
